// Whose offline writes are these? (review 2026-10-17)
//
// The offline queues (lib/offlineQueue.ts, lib/offlineSync.ts — localStorage
// and IndexedDB) replay whatever is in them with whoever is signed in NOW.
// Sign-out wipes them, but one account can replace another without a
// sign-out (a magic link or confirmation link opened on a phone where someone
// else is signed in), and the next replay then wrote the first person's
// queued taps into the second person's account. Every entry is now stamped
// with the GoTrue user id of the session that queued it, and a flush only
// sends entries that belong to the session user.
//
// Entries queued by bundles from before this change carry no owner. They are
// treated as the current user's (what every earlier bundle did) when the queue
// is flushed, because on the normal path — same person, app updated with taps
// still queued — dropping them would lose real data. On a DETECTED account
// switch (hooks/useAuth.ts dropOtherAccountsCache) everything not stamped
// with the new account is purged, ownerless entries included: they were
// queued before the switch, i.e. by the previous account.

/** GoTrue's storageKey (lib/auth.ts). The saved session JSON carries `user.id`. */
const SESSION_KEY = "trybeup_session";

let knownOwner: string | null = null;

/** useAuth tells us who is signed in, for when storage can't be read. */
export function noteOfflineOwner(userId: string | null | undefined): void {
  knownOwner = userId || null;
}

function storedSessionUser(): string | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { user?: { id?: unknown } } | null;
    const id = parsed?.user?.id;
    return typeof id === "string" && id ? id : null;
  } catch {
    return null;
  }
}

/** The signed-in user's GoTrue id, or null when nobody is (or we can't tell). */
export function currentOfflineOwner(): string | null {
  return storedSessionUser() ?? knownOwner;
}

export type OwnerVerdict = "replay" | "purge" | "hold";

/**
 * What a flush does with an entry:
 *  - "replay" — it belongs to the session user (or carries no owner: legacy);
 *  - "purge"  — it belongs to someone else: drop it, never send it;
 *  - "hold"   — it has an owner but nobody is signed in: keep it, don't send.
 */
export function ownerVerdict(owner: unknown, current: string | null): OwnerVerdict {
  if (typeof owner !== "string" || !owner) return "replay";
  if (!current) return "hold";
  return owner === current ? "replay" : "purge";
}

/** On a detected account switch: keep only entries stamped with `userId`. */
export function keptAfterSwitch(owner: unknown, userId: string): boolean {
  return typeof owner === "string" && owner === userId;
}
